import type React from "react";
import {
	createContext,
	type ReactNode,
	useContext,
	useMemo,
	useState,
} from "react";
import type {
	Comment,
	CommenterDefinition,
	Session,
	SessionSummary,
} from "../../types/core";

interface SessionState {
	isRunning: boolean;
	isPaused: boolean;
	currentSessionId: string | null;
	status: string;
	statusType: "ready" | "running" | "error";
	summary: SessionSummary | null;
	lastSession: Session | null;
	comments: Comment[];
	commenterDefinitions: CommenterDefinition[];
	lowConfidenceThreshold: number;
}

const SessionContext = createContext<
	| {
			session: SessionState;
			setSession: React.Dispatch<React.SetStateAction<SessionState>>;
	  }
	| undefined
>(undefined);

export const SessionProvider: React.FC<{ children: ReactNode }> = ({
	children,
}) => {
	const [session, setSession] = useState<SessionState>({
		isRunning: false,
		isPaused: false,
		currentSessionId: null,
		status: "初期化中...",
		statusType: "ready",
		summary: null,
		lastSession: null,
		comments: [],
		commenterDefinitions: [],
		lowConfidenceThreshold: 0.9,
	});

	// Performance optimization: Memoize the context value to prevent unnecessary re-renders of all consumers
	// when the provider itself re-renders but the state/dispatch haven't changed.
	const contextValue = useMemo(() => ({ session, setSession }), [session]);

	return (
		<SessionContext.Provider value={contextValue}>
			{children}
		</SessionContext.Provider>
	);
};

export const useSession = () => {
	const context = useContext(SessionContext);
	if (!context) {
		throw new Error("useSession must be used within a SessionProvider");
	}
	return context;
};
